import { parseISODate } from "./date";
import { getPairPreset } from "./pair-presets";
import type {
  ActualContractPair,
  ContractRef,
  EnabledCanolaMonthCode,
  FuturesMonthCode,
  ISODate,
  PairPresetId,
} from "./types";

interface MappingRule {
  readonly startMonth: number;
  readonly endMonth: number;
  readonly canolaMonth: EnabledCanolaMonthCode;
  readonly comparatorMonthByPair: Readonly<Record<PairPresetId, FuturesMonthCode>>;
  readonly yearOffset: 0 | 1;
}
const RULES: readonly MappingRule[] = [
  {
    startMonth: 1,
    endMonth: 2,
    canolaMonth: "H",
    comparatorMonthByPair: {
      "canola-soybean": "H",
      "canola-soybean-oil": "H",
      "canola-matif-rapeseed": "K",
    },
    yearOffset: 0,
  },
  {
    startMonth: 3,
    endMonth: 4,
    canolaMonth: "K",
    comparatorMonthByPair: {
      "canola-soybean": "K",
      "canola-soybean-oil": "K",
      "canola-matif-rapeseed": "K",
    },
    yearOffset: 0,
  },
  {
    startMonth: 5,
    endMonth: 6,
    canolaMonth: "N",
    comparatorMonthByPair: {
      "canola-soybean": "N",
      "canola-soybean-oil": "N",
      "canola-matif-rapeseed": "Q",
    },
    yearOffset: 0,
  },
  {
    startMonth: 7,
    endMonth: 10,
    canolaMonth: "X",
    comparatorMonthByPair: {
      "canola-soybean": "X",
      "canola-soybean-oil": "Z",
      "canola-matif-rapeseed": "X",
    },
    yearOffset: 0,
  },
  {
    startMonth: 11,
    endMonth: 12,
    canolaMonth: "F",
    comparatorMonthByPair: {
      "canola-soybean": "F",
      "canola-soybean-oil": "F",
      "canola-matif-rapeseed": "G",
    },
    yearOffset: 1,
  },
] as const;

export function resolveAutoMapping(pairId: PairPresetId, asOf: ISODate): ActualContractPair {
  const { year, month } = parseISODate(asOf);
  const rule = RULES.find(({ startMonth, endMonth }) => month >= startMonth && month <= endMonth);
  if (!rule) {
    throw new Error(`No auto-mapping rule for month ${month}`);
  }

  const preset = getPairPreset(pairId);
  const contractYear = year + rule.yearOffset;
  const canola: ContractRef = {
    instrumentId: "ICE_CANOLA",
    monthCode: rule.canolaMonth,
    year: contractYear,
  };
  const comparator: ContractRef = {
    instrumentId: preset.canolaLeg === "legA" ? preset.legB : preset.legA,
    monthCode: rule.comparatorMonthByPair[pairId],
    year: contractYear,
  };
  return {
    pairId,
    legA: preset.canolaLeg === "legA" ? canola : comparator,
    legB: preset.canolaLeg === "legB" ? canola : comparator,
  };
}

export function formatContract(contract: ContractRef): string {
  return `${contract.monthCode}${String(contract.year % 100).padStart(2, "0")}`;
}

export function formatActivePair(pair: ActualContractPair): string {
  return `${formatContract(pair.legA)} / ${formatContract(pair.legB)}`;
}

export type MappingState = AutoMappingState | ManualMappingState;

export interface AutoMappingState {
  readonly mode: "auto";
  readonly pairId: PairPresetId;
  readonly asOf: ISODate;
  readonly activePair: ActualContractPair;
}

export interface ManualMappingState {
  readonly mode: "manual";
  readonly pairId: PairPresetId;
  readonly activePair: ActualContractPair;
}

export function createAutoMappingState(pairId: PairPresetId, asOf: ISODate): AutoMappingState {
  return { mode: "auto", pairId, asOf, activePair: resolveAutoMapping(pairId, asOf) };
}

function validateManualPair(pairId: PairPresetId, pair: ActualContractPair): void {
  const preset = getPairPreset(pairId);
  if (pair.pairId !== pairId || pair.legA.instrumentId !== preset.legA || pair.legB.instrumentId !== preset.legB) {
    throw new Error("Manual contracts do not match the selected pair preset.");
  }
  if (pair.legA.monthCode === "U" || pair.legB.monthCode === "U") {
    throw new Error("September (U) is disabled in the initial product universe.");
  }
  const canola = preset.canolaLeg === "legA" ? pair.legA : pair.legB;
  const comparator = preset.canolaLeg === "legA" ? pair.legB : pair.legA;
  if (!preset.enabledCanolaMonths.includes(canola.monthCode as EnabledCanolaMonthCode)) {
    throw new Error(`Canola ${canola.monthCode} is not enabled for this product.`);
  }
  if (!preset.enabledComparatorMonths.includes(comparator.monthCode)) {
    throw new Error(`${comparator.monthCode} is not enabled for ${pairId}.`);
  }
}

export function setManualMapping(
  state: MappingState,
  pair: ActualContractPair,
): ManualMappingState {
  validateManualPair(state.pairId, pair);
  return { mode: "manual", pairId: state.pairId, activePair: pair };
}

/** Calendar changes only refresh Auto mode. Manual selection is deliberately sticky. */
export function updateMappingAsOf(state: MappingState, asOf: ISODate): MappingState {
  parseISODate(asOf);
  if (state.mode === "manual") {
    return state;
  }
  return createAutoMappingState(state.pairId, asOf);
}

export function enableAutoMapping(state: MappingState, asOf: ISODate): AutoMappingState {
  return createAutoMappingState(state.pairId, asOf);
}
